import DashboardDocumentFilterNotice from "../documents/DashboardDocumentFilterNotice";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import { getItem, getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CCollapse,
  CDataTable,
  CInput,
  CInputGroup,
  CInputGroupText,
  CLabel,
  CPagination,
  CRow,
  CSelect,
  CTooltip,
} from "@coreui/react";

import moment from "moment";

import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";

import { BsFillPlusCircleFill } from "react-icons/bs";
import CreditNoteDeleteModal from "./CreditNoteDeleteModal";
import {
  CREDIT_NOTE_STATUS_CHOICES,
  CreditNote,
  formatCreditNoteNumber,
} from "../../models/credit-note";
import { formatToCurrency } from "../../currency/format";
import CreditNoteCancellationModal from "./CreditNoteCancellationModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { formatUTCDateTime } from "../../utils/dates";
import CreditNoteConsultByCdcModal from "./CreditNoteConsultByCdcModal";
import CreditNoteResendModal from "./CreditNoteResendModal";
import CreditNoteCrudActions from "./CreditNoteCrudActions";
import CreditNoteCrudStatus from "./CreditNoteCrudStatus";
import { getObjectMinioUrl } from "../../minio/urls";
import {
  SET_ENVIRONMENT_CHOICES,
  SET_ENVIRONMENT_DEVELOPMENT,
  SET_ENVIRONMENT_PRODUCTION,
} from "../../models/set-environment";
import CreditNoteSendEmailModal from "./CreditNoteSendEmailModal";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import DocumentPrintButton from "../documents/DocumentPrintButton";
import {
  CREDIT_NOTES_CREATE,
  CREDIT_NOTES_RETRIEVE,
} from "../../auth/permissions";
import CreditNoteCancellationRetryModal from "./CreditNoteCancellationRetryModal";
import CreditNoteCancellationRevertModal from "./CreditNoteCancellationRevertModal";
import Establishment from "../../models/establishment";
import DispatchPoint from "../../models/dispatch-point";
import EstablishmentMultiSelect from "../establishments/EstablishmentMultiSelect";
import DispatchPointMultiSelect from "../dispatch-point/DispatchPointMultiSelect";
import { useIsMobile } from "../../hooks/is-mobile";
import { CreditNoteCard } from "./CreditNoteMobileCard";
import { ElectronicDocumentHistoryListModal } from "../electronic-document-history";
import CreditNoteDetailModal from "./CreditNoteDetailModal";
import SystemNotificationBanners from "../notifications/SystemNotificationBanners";

const ITEMS_PER_PAGE = 20;

const CreditNoteCrud = () => {
  const fields = [
    { key: "creditNoteDate", _classes: ["text-center"], label: "Fecha" },
    {
      key: "creditNoteNumber",
      _classes: ["text-center"],
      label: "Nro de Documento",
    },
    { key: "clientRuc", _classes: ["text-center"], label: "RUC" },
    {
      key: "clientSocialName",
      _classes: ["text-center"],
      label: "Razón Social",
    },
    { key: "amount", _classes: ["text-center"], label: "Monto" },
    { key: "status", _classes: ["text-center"], label: "Estado" },
    { key: "files", _classes: ["text-center"], label: "Archivos" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  // Filters
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [setEnvironment, setSetEnvironment] = useState<string | undefined>(
    undefined,
  );
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [dispatchPoints, setDispatchPoints] = useState<DispatchPoint[]>([]);

  const [loading, setLoading] = useState(true);
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // credit note control
  const [showCreditNoteDeleteModal, setShowCreditNoteDeleteModal] =
    useState(false);
  const [deletingCreditNote, setDeletingCreditNote] = useState<
    CreditNote | undefined
  >(undefined);

  // Cancel control
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancelling, setCancelling] = useState<CreditNote | undefined>(
    undefined,
  );

  // Consult by cdc control
  const [showConsultByCdcModal, setShowConsultByCdcModal] = useState(false);
  const [consulting, setConsulting] = useState<CreditNote | undefined>(
    undefined,
  );

  // Resend control
  const [showResendModal, setShowResendModal] = useState(false);
  const [resending, setResending] = useState<CreditNote | undefined>(undefined);

  // Send Email control
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<CreditNote | undefined>(
    undefined,
  );

  // Retry cancel control
  const [showCancellationRetryModal, setShowCancellationRetryModal] =
    useState(false);
  const [retryCancel, setRetryCancel] = useState<CreditNote | undefined>(
    undefined,
  );

  // Revert cancel control
  const [showCancellationRevertModal, setShowCancellationRevertModal] =
    useState(false);
  const [revertCancel, setRevertCancel] = useState<CreditNote | undefined>(
    undefined,
  );

  // Detail control
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCreditNote, setDetailCreditNote] = useState<
    CreditNote | undefined
  >(undefined);

  // History control
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyCreditNote, setHistoryCreditNote] = useState<
    CreditNote | undefined
  >(undefined);

  // Filter Collapse control
  const [filterCollapse, setFilterCollapse] = useState(false);
  const toggleFilterCollapse = (e: React.ChangeEvent<HTMLButtonElement>) => {
    setFilterCollapse(!filterCollapse);
    e.preventDefault();
  };

  const history = useHistory();
  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    let search = urlParams.get("search") ? urlParams.get("search") : "";

    let startDateStr = urlParams.get("start_date")
      ? urlParams.get("start_date")
      : null;

    let startDate: Date | null = null;
    if (startDateStr !== null && startDateStr !== "") {
      startDate = new Date(startDateStr);
    }

    let endDateStr = urlParams.get("end_date")
      ? urlParams.get("end_date")
      : null;

    let endDate: Date | null = null;
    if (endDateStr !== null && endDateStr !== "") {
      endDate = new Date(endDateStr);
    }

    let setEnvironmentStr = urlParams.get("set_environment");

    let setEnvironment: string | undefined;
    if (setEnvironmentStr !== null) {
      setEnvironment = setEnvironmentStr;
    }

    let statusStr = urlParams.get("status");

    let status: string | undefined;
    if (statusStr !== null) {
      status = statusStr;
    }

    let establishmentCodes = urlParams.getAll("establishment_code");

    let dispatchPointCodes = urlParams.getAll("dispatch_point");

    return {
      page: page,
      search: search,
      startDate: startDate,
      endDate: endDate,
      setEnvironment: setEnvironment,
      status: status,
      establishmentCodes: establishmentCodes,
      dispatchPointCodes: dispatchPointCodes,
    };
  };

  const fetchCreditNotes = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const additionalParams = new Map<string, string | string[]>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());

    if (urlParams.search !== null && urlParams.search !== "") {
      additionalParams.set("search", urlParams.search);
    }

    if (urlParams.startDate !== null) {
      additionalParams.set("start_date", urlParams.startDate.toISOString());
    }

    if (urlParams.endDate !== null) {
      additionalParams.set("end_date", urlParams.endDate.toISOString());
    }

    if (urlParams.setEnvironment !== undefined) {
      additionalParams.set("set_environment", urlParams.setEnvironment);
    }

    if (urlParams.status !== undefined) {
      additionalParams.set("status", urlParams.status);
    }

    if (urlParams.establishmentCodes.length > 0) {
      additionalParams.set("establishment_code", urlParams.establishmentCodes);
    }

    if (urlParams.dispatchPointCodes.length > 0) {
      additionalParams.set("dispatch_point", urlParams.dispatchPointCodes);
    }

    const documentId = new URLSearchParams(history.location.search).get(
      "document_id",
    );
    if (documentId) additionalParams.set("document_id", documentId);
    const dashboardMetric = new URLSearchParams(history.location.search).get(
      "dashboard_metric",
    );
    if (dashboardMetric) additionalParams.set("dashboard_metric", dashboardMetric);

    setSearch(urlParams.search ? urlParams.search : "");
    setStartDate(urlParams.startDate);
    setEndDate(urlParams.endDate);
    setSetEnvironment(urlParams.setEnvironment);
    setStatus(urlParams.status);

    const creditNotesState = await getList<CreditNote>(
      "/credit_notes/",
      limit,
      offset,
      additionalParams,
    );

    if (creditNotesState.status === SUCCESS) {
      if (creditNotesState.data !== undefined) {
        const count = creditNotesState.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setCreditNotes(creditNotesState.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = creditNotesState.detail
        ? creditNotesState.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    history.push(getRoutePath("addCreditNote"));
  };

  const onUpdateClick = (creditNote: CreditNote) => {
    const baseUrl = getRoutePath("addCreditNote");
    history.push(`${baseUrl}?id=${creditNote.id!}`);
  };

  const onReadClick = (creditNote: CreditNote) => {
    setDetailCreditNote(creditNote);
    setShowDetailModal(true);
  };

  const onDeleteClick = (creditNote: CreditNote) => {
    setDeletingCreditNote(creditNote);
    setShowCreditNoteDeleteModal(true);
  };

  useEffect(() => {
    fetchCreditNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("creditNotes")) {
        return;
      }
      fetchCreditNotes();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, taxPayer]);

  useEffect(() => {
    setLoading(true);
    fetchCreditNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onDeleteCancel = () => {
    setShowCreditNoteDeleteModal(false);
  };

  const onDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowCreditNoteDeleteModal(false);
    setDeletingCreditNote(undefined);
    setLoading(true);
    fetchCreditNotes();
  };

  const onCancellationClick = (creditNote: CreditNote) => {
    setCancelling(creditNote);
    setShowCancellationModal(true);
  };

  const onCancellationCancel = () => {
    setShowCancellationModal(false);
  };

  const onCancellationSuccess = () => {
    successAlert("Registro anulado con éxito!");
    setShowCancellationModal(false);
    setCancelling(undefined);
    setLoading(true);
    fetchCreditNotes();
  };

  const onCancellationRetryClick = (creditNote: CreditNote) => {
    setRetryCancel(creditNote);
    setShowCancellationRetryModal(true);
  };

  const onCancellationRetryCancel = () => {
    setShowCancellationRetryModal(false);
  };

  const onCancellationRetrySuccess = () => {
    successAlert("Anulación reintentada con éxito!");
    setShowCancellationRetryModal(false);
    setRetryCancel(undefined);
    setLoading(true);
    fetchCreditNotes();
  };

  const onCancellationRevertClick = (creditNote: CreditNote) => {
    setRevertCancel(creditNote);
    setShowCancellationRevertModal(true);
  };

  const onCancellationRevertCancel = () => {
    setShowCancellationRevertModal(false);
  };

  const onCancellationRevertSuccess = () => {
    successAlert("Anulación revertida con éxito!");
    setShowCancellationRevertModal(false);
    setRevertCancel(undefined);
    setLoading(true);
    fetchCreditNotes();
  };

  const onConsultByCdcClick = (creditNote: CreditNote) => {
    setConsulting(creditNote);
    setShowConsultByCdcModal(true);
  };

  const onConsultByCdcCancel = () => {
    setShowConsultByCdcModal(false);
  };

  const onConsultByCdcSuccess = () => {
    successAlert("NC consultada con éxito!");
    setShowConsultByCdcModal(false);
    setConsulting(undefined);
    setLoading(true);
    fetchCreditNotes();
  };

  const onResendClick = (creditNote: CreditNote) => {
    setResending(creditNote);
    setShowResendModal(true);
  };

  const onResendCancel = () => {
    setShowResendModal(false);
  };

  const onResendSuccess = () => {
    successAlert("NC reenviada con éxito!");
    setShowResendModal(false);
    setResending(undefined);
    setLoading(true);
    fetchCreditNotes();
  };

  const onSendEmailClick = (creditNote: CreditNote) => {
    setSendingEmail(creditNote);
    setShowSendEmailModal(true);
  };

  const onSendEmailCancel = () => {
    setShowSendEmailModal(false);
  };

  const onSendEmailSuccess = () => {
    successAlert("Envío de correo solicitado con éxito!");
    setShowSendEmailModal(false);
    setSendingEmail(undefined);
    setLoading(true);
    fetchCreditNotes();
  };

  const onHistoryClick = (creditNote: CreditNote) => {
    setHistoryCreditNote(creditNote);
    setShowHistoryModal(true);
  };

  const onHistoryCancel = () => {
    setShowHistoryModal(false);
    setHistoryCreditNote(undefined);
  };

  const onXMLClick = async (creditNote: CreditNote) => {
    const xmlState = await getItem<{ url: string }>(
      `/credit_notes/${creditNote.id}/xml/`,
    );

    if (xmlState.status !== SUCCESS || xmlState.data === undefined) {
      errorAlert("Error al tratar de obtener el XML");
      return;
    }
    window.open(getObjectMinioUrl(xmlState.data.url), "_blank");
  };

  const onPDFClick = async (creditNote: CreditNote) => {
    const pdfState = await getItem<{ url: string }>(
      `/credit_notes/${creditNote.id}/pdf/`,
    );

    if (pdfState.status !== SUCCESS || pdfState.data === undefined) {
      let message = "Error al tratar de obtener el PDF";
      if (pdfState.detail !== undefined) {
        message = pdfState.detail;
      }
      errorAlert(message);
      return;
    }
    window.open(getObjectMinioUrl(pdfState.data.url), "_blank");
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onStartDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    const date = new Date((value as moment.Moment).toISOString());

    setStartDate(date);
  };

  const onEndDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    const date = new Date((value as moment.Moment).toISOString());

    setEndDate(date);
  };

  const onSetEnvironmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSetEnvironment = e.target.value;

    if (
      newSetEnvironment === SET_ENVIRONMENT_DEVELOPMENT ||
      newSetEnvironment === SET_ENVIRONMENT_PRODUCTION
    ) {
      setSetEnvironment(newSetEnvironment);
    } else {
      setSetEnvironment(undefined);
    }
  };

  const onStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.value;

    if (CREDIT_NOTE_STATUS_CHOICES.has(newStatus)) {
      setStatus(newStatus);
    } else {
      setStatus(undefined);
    }
  };

  const onEstablishmentsChange = (newEstablishments: Establishment[]) => {
    setEstablishments(newEstablishments);
    if (newEstablishments.length > 0) {
      const establishmentIds = new Set(newEstablishments.map((est) => est.id));
      setDispatchPoints((current) =>
        current.filter((dp) => establishmentIds.has(dp.establishmentId))
      );
    }
  };

  const onDispatchPointsChange = (newDispatchPoints: Establishment[]) => {
    setDispatchPoints(newDispatchPoints);
  };

  const onSearch = useCallback(() => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);

    if (search !== undefined) {
      urlParams.set("search", search);
    } else {
      urlParams.delete("search");
    }

    if (startDate !== null) {
      urlParams.set("start_date", startDate.toISOString());
    } else {
      urlParams.delete("start_date");
    }

    if (endDate !== null) {
      urlParams.set("end_date", endDate.toISOString());
    } else {
      urlParams.delete("end_date");
    }

    if (setEnvironment !== undefined) {
      urlParams.set("set_environment", setEnvironment);
    } else {
      urlParams.delete("set_environment");
    }

    if (status !== undefined) {
      urlParams.set("status", status);
    } else {
      urlParams.delete("status");
    }

    urlParams.delete("establishment_code");

    if (establishments.length > 0) {
      for (const establishment of establishments) {
        if (establishment.code === undefined) {
          continue;
        }
        urlParams.append("establishment_code", establishment.code);
      }
    }

    urlParams.delete("dispatch_point_identifier");
    urlParams.delete("dispatch_point");

    if (dispatchPoints.length > 0) {
      for (const dispatchPoint of dispatchPoints) {
        const establishmentCode =
          dispatchPoint.establishment?.code ??
          dispatchPoint["establishment.code"];
        if (
          establishmentCode === undefined ||
          dispatchPoint.identifier === undefined
        ) {
          continue;
        }
        urlParams.append(
          "dispatch_point",
          `${establishmentCode}-${dispatchPoint.identifier}`,
        );
      }
    }

    let url = `?${urlParams.toString()}`;
    history.push(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    startDate,
    endDate,
    setEnvironment,
    status,
    establishments,
    dispatchPoints,
  ]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  const onDownloadKudesClick = useCallback(async () => {
    let additionalParams = new Map<string, string>();

    additionalParams.set("tax_payer_id", taxPayer.id!.toString());

    if (search !== undefined && search !== "") {
      additionalParams.set("search", search);
    }

    if (startDate !== null) {
      additionalParams.set("start_date", startDate.toISOString());
    }
    if (endDate !== null) {
      additionalParams.set("end_date", endDate.toISOString());
    }

    if (setEnvironment !== undefined && setEnvironment !== "") {
      additionalParams.set("set_environment", setEnvironment);
    }

    if (status !== undefined && status !== "") {
      additionalParams.set("status", status);
    }

    const pdfState = await getItem<{ url: string }>(
      "/credit_notes/kudes/",
      additionalParams,
    );

    if (pdfState.status !== SUCCESS || pdfState.data === undefined) {
      let message = "Error al tratar de obtener el PDF";
      if (pdfState.detail !== undefined) {
        message = pdfState.detail;
      }
      errorAlert(message);
      return;
    }
    window.open(getObjectMinioUrl(pdfState.data.url), "_blank");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    taxPayer,
    search,
    startDate,
    endDate,
    setEnvironment,
    status,
    establishments,
    dispatchPoints,
  ]);

  const onDownloadExcelClick = useCallback(async () => {
    let additionalParams = new Map<string, string>();

    additionalParams.set("tax_payer_id", taxPayer.id!.toString());

    if (search !== undefined && search !== "") {
      additionalParams.set("search", search);
    }

    if (startDate !== null) {
      additionalParams.set("start_date", startDate.toISOString());
    }
    if (endDate !== null) {
      additionalParams.set("end_date", endDate.toISOString());
    }

    if (setEnvironment !== undefined && setEnvironment !== "") {
      additionalParams.set("set_environment", setEnvironment);
    }

    if (status !== undefined && status !== "") {
      additionalParams.set("status", status);
    }

    const excelState = await getItem<{ url: string }>(
      "/credit_notes/excel/",
      additionalParams,
    );

    if (excelState.status !== SUCCESS || excelState.data === undefined) {
      let message = "Error al tratar de obtener el Excel";
      if (excelState.detail !== undefined) {
        message = excelState.detail;
      }
      errorAlert(message);
      return;
    }
    window.open(getObjectMinioUrl(excelState.data.url), "_blank");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer, search, startDate, endDate, setEnvironment, status]);

  const isMobile = useIsMobile();

  return (
    <>
      <DashboardDocumentFilterNotice />
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Notas de Crédito</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent
                  permissionName={CREDIT_NOTES_CREATE}
                >
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <SystemNotificationBanners />
            <CCardBody>
              <CRow>
                <CCol>
                  <CButton onClick={toggleFilterCollapse} className={"pl-0 filter-toggle"}>
                    <h5>
                      <i className="fa fa-filter mr-2"></i>
                      {filterCollapse ? "Ocultar Filtros" : "Ver Filtros"}
                    </h5>
                  </CButton>
                </CCol>
              </CRow>
              <CCollapse show={filterCollapse}>
                <CRow className={"mt-2"}>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Desde:</CLabel>
                  </CCol>
                  <CCol>
                    <Datetime
                      className="pl-0"
                      onChange={onStartDateChange}
                      value={startDate !== null ? startDate : ""}
                      locale="es/PY"
                      dateFormat="DD/MM/YYYY"
                      timeFormat="HH:mm"
                      closeOnSelect={true}
                      utc
                    ></Datetime>
                  </CCol>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Hasta:</CLabel>
                  </CCol>
                  <CCol>
                    <Datetime
                      className="pl-0"
                      onChange={onEndDateChange}
                      value={endDate !== null ? endDate : ""}
                      locale="es/PY"
                      dateFormat="DD/MM/YYYY"
                      timeFormat="HH:mm"
                      closeOnSelect={true}
                      utc
                    ></Datetime>
                  </CCol>
                </CRow>

                <CRow className="mb-2 mt-2">
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Ambiente SET:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CSelect
                      type="text"
                      value={setEnvironment}
                      onChange={onSetEnvironmentChange}
                    >
                      <option value={""}>-----</option>
                      {Array.from(SET_ENVIRONMENT_CHOICES.entries()).map(
                        (entry) => {
                          return (
                            <option key={entry[0]} value={entry[0]}>
                              {entry[1]}
                            </option>
                          );
                        },
                      )}
                    </CSelect>
                  </CCol>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Estado:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CSelect
                      type="text"
                      value={setEnvironment}
                      onChange={onStatusChange}
                    >
                      <option value={""}>-----</option>
                      {Array.from(CREDIT_NOTE_STATUS_CHOICES.entries()).map(
                        (entry) => {
                          return (
                            <option key={entry[0]} value={entry[0]}>
                              {entry[1]}
                            </option>
                          );
                        },
                      )}
                    </CSelect>
                  </CCol>
                </CRow>

                <CRow className="mb-2 mt-2">
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Establecimiento:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <EstablishmentMultiSelect
                      onChange={onEstablishmentsChange}
                      value={establishments}
                    />
                  </CCol>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Punto de Expedición:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <DispatchPointMultiSelect
                      onChange={onDispatchPointsChange}
                      value={dispatchPoints}
                      establishmentIds={establishments
                        .map((est) => est.id)
                        .filter((id): id is number => id !== undefined)}
                    />
                  </CCol>
                </CRow>

                <CRow className="mb-4">
                  <CCol>
                    <CLabel
                      className="visually-hidden"
                      htmlFor="autoSizingInputGroup"
                    ></CLabel>
                    <CInputGroup>
                      <CInputGroupText>
                        <i className="fa fa-search"></i>
                      </CInputGroupText>
                      <CInput
                        type="text"
                        placeholder="Introduzca el termino de búsqueda"
                        onChange={onSearchChange}
                        value={search}
                        onKeyDown={handleKeyDown}
                      />
                      <CButton
                        type="button"
                        color="primary"
                        variant="outline"
                        onClick={onSearch}
                      >
                        Filtrar
                      </CButton>
                    </CInputGroup>
                  </CCol>
                </CRow>
              </CCollapse>

              <CRow className="mb-2">
                <CCol>
                  <CButtonGroup>
                    <CButton
                      color="danger"
                      className="fa fa-file-pdf"
                      onClick={onDownloadKudesClick}
                    ></CButton>

                    <CButton
                      color="success"
                      className="fa fa-file-excel"
                      title="Descargar Excel"
                      aria-label="Descargar Excel"
                      onClick={onDownloadExcelClick}
                    ></CButton>
                  </CButtonGroup>
                </CCol>
              </CRow>

              <div>
                {isMobile ? (
                  // Mobile view: collapsible cards
                  <>
                    {creditNotes.length === 0 ? (
                      <h2 className="text-center">
                        No se encontraron registros
                      </h2>
                    ) : (
                      creditNotes.map((entry) => (
                        <CreditNoteCard
                          key={entry.id}
                          creditNote={entry}
                          onXMLClick={onXMLClick}
                          onPDFClick={onPDFClick}
                          onCancellationClick={onCancellationClick}
                          onCancellationRetryClick={onCancellationRetryClick}
                          onCancellationRevertClick={onCancellationRevertClick}
                          onReadClick={onReadClick}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                          onConsultByCdcClick={onConsultByCdcClick}
                          onResendClick={onResendClick}
                          onSendEmailClick={onSendEmailClick}
                          onHistoryClick={onHistoryClick}
                        />
                      ))
                    )}
                  </>
                ) : (
                  <CDataTable
                    noItemsView={{
                      noItems: "No se encontraron registros",
                      noResults: "No se encontraron registros",
                    }}
                    addTableClasses={"table-fixed"}
                    fields={fields}
                    items={creditNotes}
                    striped
                    border
                    loading={loading}
                    responsive
                    scopedSlots={{
                      creditNoteDate: (item: CreditNote) => {
                        return (
                          <td className="text-center">
                            {formatUTCDateTime(new Date(item.creditNoteDate!))}
                          </td>
                        );
                      },
                      creditNoteNumber: (item: CreditNote) => {
                        return (
                          <td
                            className="text-center"
                            style={{ verticalAlign: "middle" }}
                          >
                            <div>
                              {item.creditNoteNumber !== undefined
                                ? formatCreditNoteNumber(item)
                                : "-"}
                            </div>
                            {item.stampIdentifier && (
                              <small className="text-muted">
                                {"Timbrado: " + item.stampIdentifier}
                              </small>
                            )}
                          </td>
                        );
                      },
                      clientRuc: (item: CreditNote) => (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          {item.clientRuc || "-"}
                        </td>
                      ),
                      clientSocialName: (item: CreditNote) => (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          {item.clientSocialName || "-"}
                        </td>
                      ),
                      amount: (item: CreditNote) => (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          {item.amountAfterDiscount !== undefined &&
                          item.amountAfterDiscount > 0
                            ? formatToCurrency(
                                item.amountAfterDiscount!,
                                item.currency!,
                              )
                            : formatToCurrency(item.amount!, item.currency!)}
                        </td>
                      ),
                      status: (item: CreditNote) => (
                        <CreditNoteCrudStatus creditNote={item} />
                      ),
                      files: (item: CreditNote) => {
                        let xmlButton = null;
                        let pdfButton = null;

                        if (item.xmlPath !== undefined) {
                          xmlButton = (
                            <PermissionRequiredComponent
                              permissionName={CREDIT_NOTES_RETRIEVE}
                            >
                              <CTooltip content="XML">
                                <CButton
                                  className="text-white"
                                  color="info"
                                  onClick={() => {
                                    onXMLClick(item);
                                  }}
                                >
                                  <i className="fa fa-code"></i>
                                </CButton>
                              </CTooltip>
                            </PermissionRequiredComponent>
                          );
                        }

                        if (item.pdfPath !== undefined) {
                          pdfButton = (
                            <PermissionRequiredComponent
                              permissionName={CREDIT_NOTES_RETRIEVE}
                            >
                              <CTooltip content="PDF">
                                <CButton
                                  className="text-white"
                                  color="danger"
                                  onClick={() => {
                                    onPDFClick(item);
                                  }}
                                >
                                  <i className="fa fa-file"></i>
                                </CButton>
                              </CTooltip>
                            </PermissionRequiredComponent>
                          );
                        }

                        return (
                          <td className="text-center">
                            <CButtonGroup>
                              {xmlButton}
                              {pdfButton}
                              {item.pdfPath && item.id !== undefined && <DocumentPrintButton kind="credit-note" documentId={item.id} />}
                            </CButtonGroup>
                          </td>
                        );
                      },
                      actions: (item: CreditNote) => (
                        <CreditNoteCrudActions
                          creditNote={item}
                          onCancellationClick={onCancellationClick}
                          onCancellationRetryClick={onCancellationRetryClick}
                          onCancellationRevertClick={onCancellationRevertClick}
                          onReadClick={onReadClick}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                          onConsultByCdcClick={onConsultByCdcClick}
                          onResendClick={onResendClick}
                          onSendEmailClick={onSendEmailClick}
                          onHistoryClick={onHistoryClick}
                        />
                      ),
                    }}
                  />
                )}
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CreditNoteDetailModal
        show={showDetailModal}
        creditNote={detailCreditNote}
        onCancel={() => setShowDetailModal(false)}
      />
      <CreditNoteDeleteModal
        show={showCreditNoteDeleteModal}
        onCancel={onDeleteCancel}
        onSuccess={onDeleteSuccess}
        creditNote={deletingCreditNote}
      />
      <CreditNoteCancellationModal
        show={showCancellationModal}
        onCancel={onCancellationCancel}
        onSuccess={onCancellationSuccess}
        creditNote={cancelling}
      />
      <CreditNoteCancellationRetryModal
        show={showCancellationRetryModal}
        onCancel={onCancellationRetryCancel}
        onSuccess={onCancellationRetrySuccess}
        creditNote={retryCancel}
      />
      <CreditNoteCancellationRevertModal
        show={showCancellationRevertModal}
        onCancel={onCancellationRevertCancel}
        onSuccess={onCancellationRevertSuccess}
        creditNote={revertCancel}
      />
      <CreditNoteConsultByCdcModal
        show={showConsultByCdcModal}
        onCancel={onConsultByCdcCancel}
        onSuccess={onConsultByCdcSuccess}
        creditNote={consulting}
      />
      <CreditNoteResendModal
        show={showResendModal}
        onCancel={onResendCancel}
        onSuccess={onResendSuccess}
        creditNote={resending}
      />
      <CreditNoteSendEmailModal
        show={showSendEmailModal}
        onCancel={onSendEmailCancel}
        onSuccess={onSendEmailSuccess}
        creditNote={sendingEmail}
      />
      <ElectronicDocumentHistoryListModal
        show={showHistoryModal}
        creditNoteId={historyCreditNote?.id}
        cdc={historyCreditNote?.cdc}
        onCancel={onHistoryCancel}
      />
    </>
  );
};

export default CreditNoteCrud;
